// API client.
//
// Every authenticated request carries `X-HLPT-Base: <baseId>` for the
// currently active base. For users in a single team that's the only
// base they have access to. For users in shared-roster teams (e.g.
// Guest Support + Agency Support) the AppShell renders a toggle that
// switches the active base; the picker stays single-base at any one
// moment, which keeps reads/writes simple and predictable.
//
// Pre-login endpoints (employees, pinStatus, setPin, verifyPin) use
// bare fetch — they don't have a base context yet.

let activeBase = null; // { baseId, uid, baseName }

export function setApiBase(base) {
  // Base switches must invalidate the read cache below — different team,
  // different table IDs, different records.
  clearCache();
  if (base?.baseId && base?.uid) {
    activeBase = { baseId: base.baseId, uid: base.uid, baseName: base.baseName || '' };
  } else {
    activeBase = null;
  }
}

export function getActiveBase() {
  return activeBase ? { ...activeBase } : null;
}

export function getApiBaseId() {
  return activeBase?.baseId || null;
}

async function authedFetch(url, init = {}) {
  const headers = new Headers(init.headers || {});
  if (activeBase?.baseId) headers.set('X-HLPT-Base', activeBase.baseId);
  return fetch(url, { ...init, headers });
}

// Tiny in-memory TTL cache for GET endpoints whose results are stable for
// at least a few seconds. Cuts repeat-fetch waste on common UX patterns
// like clicking back to a task you just viewed, or two components asking
// for the same sub-task list. Cleared wholesale on any mutating call —
// safer than per-key invalidation and adequate at this app's data volume.
//
// The key is scoped to the active baseId so a base-switch doesn't serve
// the previous team's cached data.
const memCache = new Map(); // key → { data, expiresAt }

function cacheKey(url) {
  return `${activeBase?.baseId || 'nobase'}::${url}`;
}
function cacheGet(url) {
  const key = cacheKey(url);
  const entry = memCache.get(key);
  if (!entry) return null;
  if (entry.expiresAt < Date.now()) { memCache.delete(key); return null; }
  return entry.data;
}
function cacheSet(url, data, ttlMs) {
  memCache.set(cacheKey(url), { data, expiresAt: Date.now() + ttlMs });
}
function clearCache() { memCache.clear(); }

async function cachedAuthedGet(url, ttlMs) {
  const hit = cacheGet(url);
  if (hit) return hit;
  const data = await authedFetch(url).then(jsonOrThrow);
  cacheSet(url, data, ttlMs);
  return data;
}


async function jsonOrThrow(res) {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Bad response from server: ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

function newIdempotencyKey(prefix) {
  const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `${prefix}_${rand}`;
}

export const api = {
  // ---- Pre-login (no X-HLPT-Base) -----------------------------------------

  employees: () => fetch('/api/employees').then(jsonOrThrow),

  authLookup: (email) =>
    fetch('/api/auth-lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).then(jsonOrThrow),

  pinStatus: uid =>
    fetch(`/api/pin?uid=${encodeURIComponent(uid)}`).then(jsonOrThrow),

  setPin: (uid, pin) =>
    fetch('/api/pin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, pin, action: 'set' }),
    }).then(jsonOrThrow),

  verifyPin: (uid, pin) =>
    fetch('/api/pin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, pin, action: 'verify' }),
    }).then(jsonOrThrow),

  // Magic-link PIN reset. requestPinReset always returns ok-shaped
  // even on unknown emails (enumeration guard), so the UI should
  // show the same "check your inbox" message regardless of whether
  // the email existed in the directory.
  requestPinReset: (email) =>
    fetch('/api/pin-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'request', email }),
    }).then(jsonOrThrow),

  // Called by App.jsx on boot when ?token=… is present in the URL.
  // Server-side this clears the PIN and returns the employee record;
  // the frontend then drops into "set new PIN" mode.
  verifyPinReset: (token) =>
    fetch('/api/pin-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'verify', token }),
    }).then(jsonOrThrow),

  // ---- Authenticated (require active base) --------------------------------

  tasks: (q, options = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (options.mine) params.set('mine', options.mine);
    // ownerName lets the server pre-filter by Task Owner name so the
    // listAll terminates after a tiny matching set, avoiding the
    // "user's old tasks pushed off the recency window" problem on busy
    // teams. The server keeps the active-status filter too.
    if (options.ownerName) params.set('ownerName', options.ownerName);
    return authedFetch(`/api/tasks?${params.toString()}`).then(jsonOrThrow);
  },

  // Cached: same IDs requested within 30s reuse the response. Common
  // pattern: user clicks task → renders → switches tabs → switches back
  // → would re-fetch without this. Sort IDs so query order doesn't bust
  // the cache key.
  tasksByIds: ids => {
    if (!ids || !ids.length) return Promise.resolve({ tasks: [] });
    const sorted = [...ids].sort();
    return cachedAuthedGet(`/api/tasks?ids=${encodeURIComponent(sorted.join(','))}`, 30_000);
  },

  // Cached: 30s TTL — sub-task names/links are stable for at least that
  // long. Status changes via queueAction clear the entire cache, so a
  // user-initiated mutation is always seen immediately.
  subtasks: (taskId, ids) => {
    const qs = ids && ids.length
      ? `ids=${encodeURIComponent([...ids].sort().join(','))}`
      : `taskId=${encodeURIComponent(taskId)}`;
    return cachedAuthedGet(`/api/subtasks?${qs}`, 30_000);
  },

  myStats: (uid, days = 7, { ownerName } = {}) => {
    const tz = typeof window !== 'undefined' ? new Date().getTimezoneOffset() : 0;
    const params = new URLSearchParams({ uid, days, tzOffsetMin: String(tz) });
    // Same pattern as api.tasks: pre-filter at Airtable by Task Owner
    // name so the listAll terminates after a tiny set. Without this
    // we paginate the whole team's Task Queue.
    if (ownerName) params.set('ownerName', ownerName);
    return authedFetch(`/api/my-stats?${params.toString()}`).then(jsonOrThrow);
  },

  queue: uid =>
    authedFetch(`/api/queue?uid=${encodeURIComponent(uid)}`).then(jsonOrThrow),

  // Searchable Task Type picker source. 5-min client cache because
  // templates rarely change; server also sets Cache-Control max-age=300.
  // `q` is optional; empty/missing returns all task types for the base.
  taskTypes: (q = '') => {
    const url = q
      ? `/api/task-types?q=${encodeURIComponent(q)}`
      : '/api/task-types';
    return cachedAuthedGet(url, 5 * 60 * 1000);
  },

  // Creates a Task Queue record from a Task Type template; the server
  // polls Airtable's automation until sub-tasks are populated (up to
  // 25s). Clears the cache on success so the next task-list fetch
  // shows the new task.
  createTask: (taskTypeId, uid) => {
    clearCache();
    return authedFetch('/api/create-task', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, taskTypeId }),
    }).then(jsonOrThrow);
  },

  // Per-base Task Queue Status options for the dropdown in TaskView.
  // 5-min cache — the locked schema changes only when lock files are
  // regenerated.
  taskStatusOptions: () =>
    cachedAuthedGet('/api/task-status-options', 5 * 60 * 1000),

  // ---- Org / Utilization (cross-base, no X-HLPT-Base required) ----------

  // /api/org-me: is this user permitted to see the Org tab? Cheap; the
  // AppShell calls it once on mount to gate tab visibility. uid is the
  // user's primary task-base record id (session.employee.id).
  orgMe: (uid) =>
    fetch(`/api/org-me?uid=${encodeURIComponent(uid)}`).then(jsonOrThrow),

  // /api/org-utilization: chart-ready tree scoped to the viewer's
  // visible set. Server enforces visibility — client never sees data
  // outside its scope. Date range defaults to last 7 days on the server.
  orgUtilization: (uid, { startDate, endDate } = {}) => {
    const params = new URLSearchParams({ uid });
    if (startDate) params.set('startDate', startDate);
    if (endDate)   params.set('endDate', endDate);
    return fetch(`/api/org-utilization?${params.toString()}`).then(jsonOrThrow);
  },

  // Directly update a task's Status field. Server validates against
  // the per-base allowed status set.
  setTaskStatus: (uid, taskId, status) => {
    clearCache();
    return authedFetch('/api/queue-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, action: 'set-task-status', taskId, status }),
    }).then(jsonOrThrow);
  },

  // Idempotent "stop whatever I have running" — used by logout to
  // ensure no orphaned time logs survive a sign-out.
  stopCurrentTimer: (uid) => {
    clearCache();
    return authedFetch('/api/queue-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, action: 'pause-current' }),
    }).then(jsonOrThrow);
  },

  completeTask: (uid, taskId) => {
    clearCache(); // mutates Task Queue + Sub-Tasks Queue + Time Logs
    return authedFetch('/api/queue-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid, action: 'complete-task', taskId,
        idempotencyKey: newIdempotencyKey('comp'),
      }),
    }).then(jsonOrThrow);
  },

  deleteTask: (uid, taskId) => {
    clearCache();
    return authedFetch('/api/queue-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid, action: 'delete-task', taskId,
        idempotencyKey: newIdempotencyKey('del'),
      }),
    }).then(jsonOrThrow);
  },

  reorderQueue: (uid, subTaskIds) => {
    // Reorder only affects the KV queue, not Airtable. The next /api/queue
    // poll returns fresh order anyway, but cached sub-task details are
    // unrelated — still safe to skip the clear here. Keeping it explicit
    // for symmetry with the other mutations.
    return authedFetch('/api/queue-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, action: 'reorder', subTaskIds }),
    }).then(jsonOrThrow);
  },

  // Generic queue mutation — any action that lands here flips state in
  // Airtable, so the read cache is cleared before the request goes out.
  queueAction: payload => {
    clearCache();
    const body = payload.idempotencyKey
      ? payload
      : { ...payload, idempotencyKey: newIdempotencyKey(payload.action || 'qa') };
    return authedFetch('/api/queue-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async res => {
      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (res.status === 409) {
        // Two distinct 409 cases on this endpoint:
        //  - { error: 'A timer is already running', running: {...} }
        //    → user has to pick conflictAction (pause/complete/cancel)
        //  - { needsConfirm: true, reason: 'parent-task-complete', ... }
        //    → user has to confirm reopening a completed task
        const err = new Error(data.error || data.message || 'Conflict');
        err.code = 'CONFLICT';
        if (data.needsConfirm) {
          err.needsConfirm = true;
          err.reason = data.reason;
          err.parentTaskId = data.parentTaskId;
          err.parentTaskName = data.parentTaskName;
          err.parentTaskStatus = data.parentTaskStatus;
          err.message = data.message || err.message;
        } else {
          err.running = data.running;
        }
        throw err;
      }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    });
  },
};

export { newIdempotencyKey };
